<template>
    <App title="Batch Receipt">
        <div class="mx-auto py-4 px-3 container">

            <div class="d-flex align-items-center justify-content-between mb-3 d-print-none">

                <button
                    class="btn btn-link btn-sm text-muted text-decoration-none d-flex align-items-center gap-1 p-0"
                    @click="router.visit(route('batch.page.group'))"
                >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
                         stroke="currentColor" stroke-width="1.8"><path d="M10 12L6 8l4-4"/></svg>
                    Back to batches
                </button>
                <div class="ms-auto d-flex align-items-center gap-2">
                    <div class="d-flex align-items-center gap-2" v-if="batch.status === 'pending' && isAdmin()">
                        <button
                            class="btn btn-danger btn-sm d-flex align-items-center gap-2"
                            style="font-size:12px;letter-spacing:.02em"
                            @click="handleDecline"
                        >
                            <i class="fa fa-cancel"></i>
                            Decline Allocation
                        </button>

                        <button class="btn btn-secondary btn-sm d-flex align-items-center gap-2"
                            style="font-size:12px;letter-spacing:.02em
                            " @click="handleApprove">
                            <i class="fa fa-plus"></i>
                        
                            Approve Allocation
                        </button>
                    </div>
                   
                    <button
                        class="btn btn-dark btn-sm d-flex align-items-center gap-2"
                        style="font-size:12px;letter-spacing:.02em"
                        :disabled="pdfLoading"
                        @click="downloadPDF"
                    >
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none"
                            stroke="currentColor" stroke-width="1.8">
                            <path d="M8 2v8M5 7l3 3 3-3M3 11v2a1 1 0 001 1h8a1 1 0 001-1v-2"/>
                        </svg>
                        {{ pdfLoading ? 'Generating…' : 'Download Receipt (PDF)' }}
                    </button>
                </div>
            </div>

            
            <!-- Here we go -->
          
            <div class="bg-white border rounded-0 overflow-hidden p-2 d-print-none">

                <!-- top accent stripe -->
                <div class="inv-stripe"></div>

                <!-- ── Header ── -->
                <div class="row g-0 px-4 pt-4 pb-3 align-items-start">
                    <div class="d-block mb-4 rounded-0 border w-100 status-pill bg-light">
                        <span class="small w-100" v-if="batch.status === 'declined'">
                            Declined: {{ batch.note ?? 'No reason provided' }}
                        </span>
                    </div>

                    <!-- left: brand + title -->
                    <div class="col">
                        <div class="d-flex align-items-center gap-2 mb-2">
                            <div class="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                                 style="width:30px;height:30px;background:#0d0e12;color:#fff">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zm-9-4h2v4h-2V3z"/>
                                </svg>
                            </div>
                            <div>
                                <p class="mb-0 fw-semibold text-uppercase lh-1"
                                   style="font-size:11.5px;letter-spacing:.08em">Warehouse</p>
                                <p class="mb-0 text-muted lh-1 mt-1"
                                   style="font-size:10px;letter-spacing:.04em">Management System</p>
                            </div>
                        </div>
                        <h1 class="mb-1 fw-black  fs-2 py-2">Allocation Receipt</h1>
                        <p class="mb-0 text-dark text-uppercase"
                           style="font-size:10.5px;letter-spacing:.08em;font-family:var(--font-mono)">
                            {{ batch.reference }}
                        </p>
                    </div>

                    <!-- right: status + dates -->
                    <div class="col-auto text-end">
                        <span class="d-inline-flex align-items-center gap-2 rounded-pill mb-2 status-pill"
                              :class="statusClass">
                            <span class="status-dot"></span>{{ batch.status }}
                        </span>
                        <div class="text-secondary lh-lg" style="font-size:11px">
                            <div>Created &nbsp;<strong class="text-dark">{{ formatDate(batch.created_at) }}</strong></div>
                            <div v-if="batch.approved_at">Approved <strong class="text-dark">{{ formatDate(batch.approved_at) }}</strong></div>
                            <div v-if="batch.declined_at">Declined &nbsp;<strong class="text-dark">{{ formatDate(batch.declined_at) }}</strong></div>
                        </div>
                    </div>

                </div>

                <!-- ── Summary dark banner ── -->
              
                <!-- ── Inventory summary card ── -->
               <!-- ── Inventory summary card ── -->
                <div class="px-4 pb-4">
                    <div class="border rounded-2 p-4 bg-white">

                        <!-- Top: count + meta -->
                        <div class="d-flex align-items-center gap-3 pb-3 mb-3 border-bottom">
                            <span class="fw-medium text-dark flex-shrink-0"
                                style="font-size:52px;line-height:1;letter-spacing:-2px">
                                {{ batch.quantity }}
                            </span>

                            <div class="vr flex-shrink-0" style="height:38px;opacity:.2"></div>

                            <div>
                                <p class="mb-0 fw-medium text-dark text-uppercase"
                                style="font-size:10px;letter-spacing:.12em">
                                    Total inventory
                                </p>
                                <p class="mb-0 text-secondary"
                                style="font-size:10px">
                                    Stock items dispatched
                                </p>
                            </div>
                        </div>

                        <!-- Bottom: type pills -->
                        <p class="mb-2 text-secondary text-uppercase fw-medium"
                        style="font-size:9px;letter-spacing:.15em">By type</p>

                        <div class="d-flex flex-wrap gap-1">
                            <span
                                v-for="entry in stockTypeSummary"
                                :key="entry.type"
                                class="d-inline-flex align-items-center gap-2 border rounded-pill px-3"
                                style="padding-top:5px;padding-bottom:5px;background:var(--bs-secondary-bg)"
                            >
                                <span class="rounded-circle bg-secondary flex-shrink-0"
                                    style="width:5px;height:5px;opacity:.5"></span>
                                <span class="text-uppercase text-secondary"
                                    style="font-size:10px;letter-spacing:.07em">
                                    {{ entry.type }}
                                </span>
                                <span class="fw-medium text-dark"
                                    style="font-size:13px;letter-spacing:-.3px">
                                    {{ entry.count }}
                                </span>
                            </span>
                        </div>

                    </div>
                </div>

                <!-- ── Meta grid ── -->
                <div class="row g-0 px-4 py-1 border-top border-bottom">
                    <div class="col-6 col-md-4 py-2">
                        <p class="mb-1 meta-label">Reference No.</p>
                        <p class="mb-0 fw-medium" style="font-size:12.5px;font-family:var(--font-mono)">
                            {{ batch.reference }}
                        </p>
                    </div>
                    <div class="col-6 col-md-4 py-2">
                        <p class="mb-1 meta-label">From Warehouse</p>
                        <p class="mb-0 fw-medium" style="font-size:12.5px">{{ batch.warehouse.title }}</p>
                    </div>
                    <div class="col-6 col-md-4 py-2">
                        <p class="mb-1 meta-label">To Station</p>
                        <p class="mb-0 fw-medium" style="font-size:12.5px">{{ batch.station.title }}</p>
                    </div>
                    <div class="col-6 col-md-4 py-2">
                        <p class="mb-1 meta-label">Assigned By</p>
                        <p class="mb-0 fw-medium" style="font-size:12.5px">
                            {{ batch.assigned_by ?? batch.admin?.fullname ?? '—' }}
                        </p>
                    </div>
                    <div class="col-6 col-md-4 py-2">
                        <p class="mb-1 meta-label">{{ approverLabel }}</p>
                        <p class="mb-0 fw-medium" style="font-size:12.5px">
                            {{ batch.action_by?.fullname ?? '—' }}
                        </p>
                    </div>
                    <div class="col-6 col-md-4 py-2" v-if="batch.location">
                        <p class="mb-1 meta-label">Location</p>
                        <p class="mb-0 fw-medium" style="font-size:12.5px">{{ batch.location }}</p>
                    </div>
                </div>

                <!-- ── Reusable StockTable ── -->
                <StockTable
                    :items="stocks"
                    :filter-types="filterTypes"
                    :loading="tableLoading"
                    :per-page="8"
                />

                <!-- ── Footer / signature ── -->
                <div class="d-flex justify-content-between align-items-end px-4 py-3 border-top">
                    <div>
                        <div class="mb-1 sig-line"></div>
                        <p class="mb-0 fw-medium" style="font-size:12.5px">
                            {{ batch?.action_by?.fullname ?? '—' }}
                        </p>
                        <p class="mb-0 text-secondary" style="font-size:11px">
                            {{ batch?.action_by?.role ?? '-' }} Manager 
                        </p>
                    </div>
                    <div class="text-end text-secondary lh-lg" style="font-size:11px">
                        <div>Generated: <strong class="text-dark">{{ today }}</strong></div>
                        <div>Warehouse Management System</div>
                    </div>
                </div>

            </div><!-- /.inv-card -->


            <!-- ╔══════════════════════════════════════╗
                 ║  PRINT-ONLY layout                   ║
                 ║  Hidden on screen via .d-none.       ║
                 ║  @media print overrides to display.  ║
                 ║  beforeprint loads all rows into     ║
                 ║  allStocks[] so every item prints.   ║
                 ╚══════════════════════════════════════╝ -->
            <div class="d-none d-print-block">
                <div class="print-doc">

                    <div class="print-header">
                        <div>
                            <p class="print-brand">Warehouse Management System</p>
                            <h1 class="print-title">Allocation Receipt</h1>
                            <p class="print-ref">{{ batch.reference }}</p>
                        </div>
                        <div style="text-align:right">
                            <p class="print-status">{{ batch.status }}</p>
                            <div class="print-dates">
                                <div>Created: {{ formatDate(batch.created_at) }}</div>
                                <div v-if="batch.approved_at">Approved: {{ formatDate(batch.approved_at) }}</div>
                                <div v-if="batch.declined_at">Declined: {{ formatDate(batch.declined_at) }}</div>
                            </div>
                        </div>
                    </div>

                    <hr class="print-rule thick mt-0">

                    <div class="print-summary-row">
                        <div>
                            <span class="pl">Total Inventory</span>
                            <span class="pv">{{ allStocks.length }} items</span>
                        </div>
                        <div>
                            <span class="pl">Route</span>
                            <span class="pv">{{ batch.warehouse.title }} → {{ batch?.station?.title }}</span>
                        </div>
                    </div>

                    <div class="print-meta-grid">
                        <div><span class="pl">Reference No.</span><span class="pv">{{ batch.reference }}</span></div>
                        <div><span class="pl">Assigned By</span><span class="pv">{{ batch.assigned_by ?? batch.admin?.fullname ?? '—' }}</span></div>
                        <div><span class="pl">{{ approverLabel }}</span><span class="pv">{{ batch?.station?.manager?.fullname ?? '—' }}</span></div>
                        <div v-if="batch.location"><span class="pl">Location</span><span class="pv">{{ batch.location }}</span></div>
                    </div>

                    <hr class="print-rule">

                    <p class="print-section-label">Stock Items ({{ allStocks.length }} total)</p>

                    <p v-if="printLoading" class="print-muted">Loading all items…</p>

                    <table v-else class="print-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Serial Number</th>
                                <th>Stock Type</th>
                                <th>Date Added</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, i) in allStocks" :key="row.id">
                                <td>{{ i + 1 }}</td>
                                <td>{{ row.serial }}</td>
                                <td>{{ row.type }}</td>
                                <td>{{ formatDate(row.created_at) }}</td>
                            </tr>
                            <tr v-if="!allStocks.length">
                                <td colspan="4" style="text-align:center;color:#aaa;padding:14pt">No items.</td>
                            </tr>
                        </tbody>
                    </table>

                    <hr class="print-rule">

                    <div class="print-footer">
                        <div>
                            <div class="print-sig-line"></div>
                            <p class="print-sig-name">{{ batch.action_by?.fullname ?? '—' }}</p>
                            <p class="print-sig-role">{{ batch.action_by?.role }}</p>
                        </div>
                        <div style="text-align:right">
                            <p class="print-muted">Generated: {{ today }}</p>
                            <p class="print-muted">Total Items: {{ allStocks.length }}</p>
                        </div>
                    </div>

                </div>
            </div><!-- /d-print-block -->

        </div>

        <!-- Notivue notifications -->
        <div class="position-fixed top-0 end-0" style="z-index:9999">
            <NotivueKeyboard v-slot="{ containersTabIndex }">
                <Notivue v-slot="item" :containersTabIndex="containersTabIndex">
                    <AppNotification :item="item" />
                </Notivue>
            </NotivueKeyboard>
        </div>
    </App>
</template>

<script lang="ts">
import App from '@/vue/templates/App.vue'
import AppNotification from '@/vue/components/AppNotification.vue'
import StockTable from '@/widget/table/StockTable.vue'
import type { StockItem } from '@/widget/table/StockTable.vue'
import { Component, Vue, toNative, Prop } from 'vue-facing-decorator'
import { Notivue, push, NotivueKeyboard } from 'notivue'
import Swal from 'sweetalert2'
import { hasRole, Role } from '@/models/Admin'

@Component({
    components: { App, AppNotification, StockTable, Notivue, NotivueKeyboard },
})
class BatchReceipt extends Vue {

    @Prop({ type: Object, required: true })
    readonly batch!: any

    stocks: StockItem[]    = []
    filterTypes: string[]  = []
    tableLoading           = false
    allStocks: StockItem[] = []
    printLoading           = false
    pdfLoading             = false

    TYPE_ACCENTS = [
    '#3b82f6', // blue
    '#10b981', // emerald
    '#f59e0b', // amber
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#14b8a6', // teal
    '#f97316', // orange
    '#6366f1', // indigo
]

    get stockTypeSummary(): { type: string; count: number }[] {
        const map: Record<string, number> = {}
        for (const item of this.stocks) {
            const key = item.type ?? 'Unknown'
            map[key] = (map[key] ?? 0) + 1
        }
        return Object.entries(map)
            .map(([type, count]) => ({ type, count }))
            .sort((a, b) => b.count - a.count)
    }

    typePercent(count: number): number {
        if (!this.stocks.length) return 0
        return Math.round((count / this.stocks.length) * 100)
    }
 
    /** Cycles through the accent palette by index. */
    typeAccent(idx: number): string {
        return this.TYPE_ACCENTS[idx % this.TYPE_ACCENTS.length]
    }

    showToast(message: string, type: 'success' | 'error' ) {
        
        Swal.fire({
            toast: true,
            position: 'top-end', 
            icon: type, 
            title: message,
            showConfirmButton: false, 
            timer: 5000, 
            timerProgressBar: true,
            didOpen: (toast) => {
                // Pause the timer when the mouse hovers over the toast
                toast.addEventListener('mouseenter', Swal.stopTimer)
                // Resume the timer when the mouse leaves
                toast.addEventListener('mouseleave', Swal.resumeTimer)
            }
        })
    }

    // ─── Lifecycle ───────────────────────────────────────────
    async mounted() {
        await Promise.all([this.loadStocks(), this.loadFilterTypes()])
        window.addEventListener('beforeprint', this.handleBeforePrint)
    }

    beforeUnmount() {
        window.removeEventListener('beforeprint', this.handleBeforePrint)
    }

    // ─── Data ────────────────────────────────────────────────
    async loadStocks() {
        this.tableLoading = true
        try {
            const res = await this.$http.get(
                this.route('batch.ajax.stocks', { batch: this.batch.id }),
                { params: { per_page: 99999 } }   // all records; StockTable paginates in JS
            )
            this.stocks = res.data.data ?? res.data
        } catch {
            push.error({ message: 'Failed to load stock items' })
        } finally {
            this.tableLoading = false
        }
    }

    isAdmin() {
        return hasRole(this.$user, Role.SUPER_ADMIN)
    }

    async handleDecline(e: Event) {
    e.preventDefault()

    const { value: note, isConfirmed } = await Swal.fire({
        title: 'Decline Allocation',
        html: `
            <p style="font-size:13px;color:#555;margin-bottom:12px">
                Please provide a reason for declining this allocation.
            </p>
            <textarea
                id="swal-decline-note"
                class="swal2-textarea mx-auto small text-sm text-muted fw-normal outline-0 border rounded-2 border-secondary"
                placeholder="Enter reason for declining…"
                style="width:100%;max-height:80px;resize:vertical"
            ></textarea>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Decline',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        focusConfirm: false,
        preConfirm: () => {
            const note = (document.getElementById('swal-decline-note') as HTMLTextAreaElement)?.value?.trim()
            if (!note) {
                Swal.showValidationMessage('A reason is required to decline.')
                return false
            }
            return note
        },
    })

    if (!isConfirmed) return

    this.$http
        .put(this.route('batch.ajax.decline', { batch: this.batch.id }), { note })
        .then(() => {
           this.showToast('Allocation declined', 'success')
            this.router.visit(this.route('batch.page.group'))
        })
        .catch(() => {
         this.showToast('Failed to decline allocation', 'error')
        })
}

async handleApprove() {
    const { isConfirmed } = await Swal.fire({
        title: 'Approve Allocation',
        text: 'Are you sure you want to approve this allocation? This action cannot be undone.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Approve',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#198754',
        cancelButtonColor: '#6c757d',

    })

    if (!isConfirmed) return

    this.$http
        .put(this.route('batch.ajax.approve', { batch: this.batch.id }))
        .then(() => {
            this.showToast('Allocation approved', 'success')
            this.router.visit(this.route('batch.page.group'))
        })
        .catch(() => {
            this.showToast('Failed to approve allocation', 'error')
        })
}

    async loadFilterTypes() {
        try {
            const res = await this.$http.get(this.route('stock.ajax.filters'))
            this.filterTypes = res.data.types ?? []
        } catch {
            console.error('Failed to load filter types')
        }
    }

    // ─── Computed ────────────────────────────────────────────
    get statusClass(): string {
        return { Approved: 's-approved', Pending: 's-pending', Declined: 's-declined' }[this.batch.status as string] ?? 's-pending'
    }

    get approverLabel(): string {
        return { approved: 'Approved By', declined: 'Declined By' }[this.batch.status as string] ?? 'Pending Approval'
    }

    get today(): string {
        return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }

    formatDate(d?: string | null): string {
        if (!d) return '—'
        return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }

    // ─── Actions ─────────────────────────────────────────────
    async downloadPDF() {
    this.pdfLoading = true
    try {
        window.open(
            this.route('batch.ajax.receipt.download', { batch: this.batch.id}),
            '_blank'
        )
    } finally {
        this.pdfLoading = false
    }
}

    /**
     * Fires before the browser opens the print dialog.
     * Fetches every row into allStocks[] so the print layout
     * renders the complete dataset — not just the current page.
     */
    async handleBeforePrint() {
        if (this.allStocks.length) return
        this.printLoading = true
        try {
            const res = await this.$http.get(
                this.route('batch.ajax.stocks', { batch: this.batch.id }),
                { params: { per_page: 99999 } }
            )
            this.allStocks = res.data.data ?? res.data
        } catch {
            push.error({ message: 'Could not load all items for print' })
        } finally {
            this.printLoading = false
        }
    }
}

export default toNative(BatchReceipt)
</script>

<style scoped>
/*
  Custom rules kept to the absolute minimum —
  only what Bootstrap 5 utilities genuinely cannot do:

  1. --font-mono / --font-serif   custom font stack tokens
  2. inv-stripe                   4px black top accent bar
  3. doc-title                    DM Serif Display italic
  4. status-pill colours          semantic greens/ambers/reds not in BS5
  5. status-dot                   6px circle, currentColor fill
  6. meta-label                   10px + letter-spacing combo
  7. sig-line                     110px partial border-top
  8. @media print block           all print-specific rules
*/

:root {
    --font-mono:  'DM Mono', 'Courier New', monospace;
    --font-serif: 'DM Serif Display', Georgia, serif;
    --ink:        #0d0e12;
    --ink-muted:  #9396a4;
    --border:     #e8e7e2;
}

.summary-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 16px 20px;
    background: #f8f8f7;
    border: 1px solid var(--border);
    min-width: 130px;
}
 
.summary-card--total {
    min-width: 160px;
    background: #f0f0ee;
    border-left: 3px solid var(--ink);
}
 
.summary-card--type {
    min-width: 130px;
    border-left: 3px solid var(--type-accent, #3b82f6);
    position: relative;
    overflow: hidden;
}
 
.summary-card--route {
    min-width: 200px;
    background: #f8f8f7;
}
 
.summary-card__label {
    margin: 0 0 4px;
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .1em;
    color: var(--ink-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
 
.summary-card__value {
    margin: 0;
    font-weight: 900;
    line-height: 1;
    color: var(--ink);
}
 
.summary-card__sub {
    margin: 6px 0 0;
    font-size: 9.5px;
    color: var(--ink-muted);
}
 
/* thin progress bar inside each type card */
.summary-card__bar {
    height: 3px;
    background: #e0e0dc;
    border-radius: 2px;
    margin: 8px 0 4px;
    overflow: hidden;
}
.summary-card__bar-fill {
    height: 100%;
    background: var(--type-accent, #3b82f6);
    border-radius: 2px;
    transition: width .4s ease;
}
 
/* skeleton shimmer */
.summary-card--skeleton {
    min-width: 130px;
    min-height: 90px;
    background: linear-gradient(90deg, #f0f0ee 25%, #e4e4e0 50%, #f0f0ee 75%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
    border-left-color: #e0e0dc;
}

/* 1 — top accent stripe */
.inv-stripe { height: 4px; background: var(--ink); }

/* 2 — editorial italic heading */
.doc-title {
    font-family: var(--font-serif);
    font-size: 27px;
    font-weight: 400;
    font-style: italic;
    line-height: 1.1;
    color: var(--ink);
}

/* 3 — status pill colours */
.status-pill {
    padding: 4px 11px;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: .07em;
    text-transform: uppercase;
    font-family: var(--font-mono);
}
.status-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
.s-approved { background: #e8f5ef; color: #1a6644; }
.s-pending  { background: #fdf3db; color: #8a5c00; }
.s-declined { background: #fdeaea; color: #8a1f1f; }

/* 4 — meta field label */
.meta-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: .09em;
    color: var(--ink-muted);
}

/* 5 — signature rule */
.sig-line { width: 110px; border-top: 1px solid var(--ink); }

/* ══════════════════════════════════════
   PRINT
   d-print-none / d-print-block handle
   show/hide. These rules only govern
   the print layout appearance.
══════════════════════════════════════ */
@media print {
    @page { size: A4; margin: 18mm 20mm; }

    .print-doc { font-family: Georgia, serif; font-size: 10pt; color: #000; }

    .print-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 10pt; }
    .print-brand  { font-size: 8pt; text-transform: uppercase; letter-spacing: 2px; color: #666; margin: 0 0 3pt; }
    .print-title  { font-size: 20pt; font-weight: 400; font-style: italic; margin: 0; }
    .print-ref    { font-size: 8pt; color: #aaa; margin: 0; letter-spacing: 2px; text-transform: uppercase; }
    .print-status { font-size: 8pt; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; margin: 0 0 4pt; }
    .print-dates  { font-size: 8pt; color: #666; line-height: 1.8; }

    /* hr overrides */
    .print-rule       { border: none; border-top: .5pt solid #ccc !important; margin: 8pt 0; opacity: 1; }
    .print-rule.thick { border-top-width: 1.5pt !important; border-top-color: #000 !important; }

    .print-summary-row { display: flex; gap: 40pt; margin-bottom: 8pt; }
    .pl { display: block; font-size: 7pt; text-transform: uppercase; letter-spacing: 1.5px; color: #999; margin-bottom: 1pt; }
    .pv { display: block; font-size: 10pt; font-weight: 700; }

    .print-meta-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 5pt 10pt; margin-bottom: 8pt; }

    .print-section-label { font-size: 7.5pt; text-transform: uppercase; letter-spacing: 2px; color: #666; margin: 0 0 6pt; }
    .print-muted { font-size: 7.5pt; color: #aaa; line-height: 1.7; margin: 0; }

    .print-table { width: 100%; border-collapse: collapse; font-size: 9pt; page-break-inside: auto; }
    .print-table thead th {
        background: #000; color: #fff;
        padding: 5pt 8pt; font-size: 7.5pt;
        text-transform: uppercase; letter-spacing: 1.5px; text-align: left;
    }
    .print-table td { padding: 5pt 8pt; border-bottom: .5pt solid #e0e0e0; }
    .print-table tbody tr:nth-child(even) td { background: #f9f9f9; }
    .print-table tr { page-break-inside: avoid; }

    .print-footer { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 14pt; }
    .print-sig-line { width: 110pt; border-top: 1pt solid #000; margin-bottom: 4pt; }
    .print-sig-name { font-size: 9.5pt; font-weight: 700; margin: 0; }
    .print-sig-role { font-size: 7.5pt; color: #777; margin: 0; }
}
</style>